import { useTranslation } from 'react-i18next'
import { Input } from '../Input/Input'
import { cx } from '../utils/cx'
import './CardFields.css'

export interface CardValue {
  name: string
  number: string
  /** MM/AA */
  expiry: string
  cvv: string
}

export type CardFieldName = keyof CardValue
export type CardErrors = Partial<Record<CardFieldName, 'required' | 'invalid'>>

export const EMPTY_CARD: CardValue = { name: '', number: '', expiry: '', cvv: '' }

/** "4111111111111111" → "4111 1111 1111 1111" (máscara de CardForm.tsx de la app actual, sin librería). */
export const formatCardNumber = (raw: string) =>
  raw
    .replace(/\D/g, '')
    .slice(0, 16)
    .replace(/(\d{4})(?=\d)/g, '$1 ')

/** "0929" / "09/29" → "09/29" */
export function formatExpiry(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4)
  return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits
}

/**
 * Validaciones de CardForm.tsx: todo obligatorio, número de 16 cifras, caducidad válida y no pasada; CVV
 * de hasta 4 cifras (CVV_MAX_LENGTH de la validación del canal). `cvv` = solo se pide el CVV (tarjeta guardada).
 */
export function validateCard(value: CardValue, mode: 'new' | 'cvv', now = new Date()): CardErrors {
  const errors: CardErrors = {}
  const cvv = value.cvv.trim()
  if (!cvv) errors.cvv = 'required'
  else if (!/^\d{3,4}$/.test(cvv)) errors.cvv = 'invalid'
  if (mode === 'cvv') return errors
  if (!value.name.trim()) errors.name = 'required'
  const digits = value.number.replace(/\D/g, '')
  if (!digits) errors.number = 'required'
  else if (digits.length !== 16) errors.number = 'invalid'
  const m = /^(\d{2})\/(\d{2})$/.exec(value.expiry)
  if (!value.expiry) errors.expiry = 'required'
  else if (!m || Number(m[1]) < 1 || Number(m[1]) > 12) errors.expiry = 'invalid'
  else {
    const endOfMonth = new Date(2000 + Number(m[2]), Number(m[1]), 1)
    if (endOfMonth <= now) errors.expiry = 'invalid'
  }
  return errors
}

export interface CardFieldsProps {
  value: CardValue
  onChange: (value: CardValue) => void
  /** Errores visibles (la pantalla decide cuándo mostrarlos) */
  errors?: CardErrors
  onBlurField?: (field: CardFieldName) => void
  mode?: 'new' | 'cvv'
  disabled?: boolean
  className?: string
}

/**
 * Campos de tarjeta del pago (la captura es un formulario propio, no del banco; inventario §4.1). Etiquetas
 * visibles, autocompletado del navegador y teclado numérico. Los datos se envían al PMS como hoy.
 */
export function CardFields({ value, onChange, errors = {}, onBlurField, mode = 'new', disabled, className }: CardFieldsProps) {
  const { t } = useTranslation()
  const message = (field: CardFieldName) => {
    const e = errors[field]
    return e === 'required' ? t('v2.legacy.validations.Required') : e === 'invalid' ? t('v2.legacy.validations.Invalid') : undefined
  }
  const set = (field: CardFieldName, v: string) => onChange({ ...value, [field]: v })
  return (
    <div className={cx('hp-cardfields', className)}>
      {mode === 'new' && (
        <>
          <Input label={t('v2.legacy.payment.Cardholder_name')} autoComplete="cc-name" value={value.name} onChange={(e) => set('name', e.target.value)} onBlur={() => onBlurField?.('name')} error={message('name')} disabled={disabled} required />
          <Input
            label={t('v2.ds.cardFields.numero')}
            autoComplete="cc-number"
            inputMode="numeric"
            className="tnum"
            value={formatCardNumber(value.number)}
            onChange={(e) => set('number', e.target.value.replace(/\D/g, '').slice(0, 16))}
            onBlur={() => onBlurField?.('number')}
            error={message('number')}
            disabled={disabled}
            required
          />
        </>
      )}
      <div className={cx('hp-cardfields__row', mode === 'cvv' && 'hp-cardfields__row--single')}>
        {mode === 'new' && (
          <Input
            label={t('v2.ds.cardFields.caducidad')}
            help={t('v2.ds.cardFields.caducidadAyuda')}
            autoComplete="cc-exp"
            inputMode="numeric"
            className="tnum"
            value={value.expiry}
            onChange={(e) => set('expiry', formatExpiry(e.target.value))}
            onBlur={() => onBlurField?.('expiry')}
            error={message('expiry')}
            disabled={disabled}
            required
          />
        )}
        <Input
          label={t('v2.legacy.labels.CVV')}
          help={t('v2.ds.cardFields.cvvAyuda')}
          autoComplete="cc-csc"
          inputMode="numeric"
          className="tnum"
          value={value.cvv}
          maxLength={4}
          onChange={(e) => set('cvv', e.target.value.replace(/\D/g, '').slice(0, 4))}
          onBlur={() => onBlurField?.('cvv')}
          error={message('cvv')}
          disabled={disabled}
          required
        />
      </div>
    </div>
  )
}
